/**
 * Bảng điều khiển – Staff án Workspace (Thư ký/Thẩm phán) hoặc Dashboard Chánh án.
 * Nội dung gộp một trang: các section xếp dọc (không dùng tab).
 */
import { useMemo, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAppSelector } from '../../hooks/redux'
import ChiefJudgeDashboard from './ChiefJudgeDashboard'
import CasesPage from '../cases/CasesPage'
import WorkspaceCasesPage from './WorkspaceCasesPage'
import CalendarPage from './CalendarPage'
import DocumentsPendingPage from './DocumentsPendingPage'
import PreliminaryChiefPage from './PreliminaryChiefPage'
import AssignCaseListPage from './AssignCaseListPage'
import WorkspaceSectionHeading from './WorkspaceSectionHeading'
import { WORKSPACE_SECTION_IDS, workspaceHash } from './workspaceSectionIds'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Divider,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { Add, Description, Campaign, Mail, Event, Download } from '@mui/icons-material'
import { brandColors } from '../../theme'
import { DASHBOARD_STAT_LABEL_SX } from '../../theme/dashboardKpiStyles'
import { mockWorkspaceTasks } from '../../mock/data'
import type { WorkspaceTask, TaskPriority, TaskActionType } from '../../types/workspaceTask'

const sectionScrollSx = { scrollMarginTop: { xs: 10, md: 11 } }

/** Tiêu đề cột ưu tiên (KHẨN CẤP / …) — cùng hệ thống với KPI */
const DASHBOARD_PRIORITY_TITLE_SX = {
  fontSize: '0.9375rem',
  fontWeight: 800,
  letterSpacing: '0.06em',
  lineHeight: 1.35,
} as const

const ACTION_ICONS: Record<TaskActionType, React.ReactNode> = {
  filing: <Description />,
  decision: <Campaign />,
  notice: <Mail />,
  session: <Event />,
  other: <Description />,
}

function getDaysLeft(dueAt: string): { text: string; color: string; overdue: boolean } {
  const due = new Date(dueAt)
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  due.setHours(0, 0, 0, 0)
  const diff = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  if (diff < 0) return { text: `Đã quá hạn ${Math.abs(diff)} ngày`, color: brandColors.secondary, overdue: true }
  if (diff === 0) return { text: 'Hạn chót: Hôm nay', color: '#dc2626', overdue: false }
  if (diff <= 2) return { text: `Còn ${diff} ngày`, color: '#dc2626', overdue: false }
  if (diff <= 5) return { text: `Còn ${diff} ngày`, color: '#ea580c', overdue: false }
  return { text: `Còn ${diff} ngày`, color: '#16a34a', overdue: false }
}

function formatDueDate(s: string) {
  return new Date(s).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function TaskTabList({ tasks, emptyLabel, compact }: { tasks: WorkspaceTask[]; emptyLabel: string; compact?: boolean }) {
  if (tasks.length === 0) {
    if (!emptyLabel) return null
    return (
      <Box sx={{ py: 4, textAlign: 'center', px: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      </Box>
    )
  }
  return (
    <List disablePadding>
      {tasks.map((task) => {
        const { text: deadlineText, color: deadlineColor, overdue } = getDaysLeft(task.due_at)
        return (
          <ListItem
            key={task.id}
            alignItems="flex-start"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              py: compact ? 1.25 : 1.75,
              px: compact ? 1.25 : 2,
              '&:last-child': { borderBottom: 0 },
            }}
          >
            <ListItemIcon sx={{ minWidth: compact ? 36 : 42, mt: 0.25, color: 'text.secondary' }}>
              {ACTION_ICONS[task.action_type]}
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography fontWeight={600} component="span" sx={{ fontSize: compact ? '0.8125rem' : undefined }}>
                  Vụ án {task.case_code} – {task.party_name}
                </Typography>
              }
              secondary={
                <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                  <Typography variant="body2" component="span" color="text.secondary" display="block">
                    Hành động: {task.action_label}
                  </Typography>
                  <Typography variant="caption" component="span" sx={{ color: deadlineColor, fontWeight: overdue ? 600 : 500 }}>
                    {overdue && '⚠ '}
                    {deadlineText} ({formatDueDate(task.due_at)})
                  </Typography>
                </Box>
              }
            />
          </ListItem>
        )
      })}
    </List>
  )
}

export default function WorkspacePage() {
  const { user } = useAppSelector((state) => state.auth)
  const navigate = useNavigate()
  const location = useLocation()
  const isChiefJudge = user?.role === 'admin' || user?.role === 'chief_judge'
  const isJudge = user?.role === 'judge'

  useEffect(() => {
    const hash = location.hash.replace(/^#/, '')
    if (!hash) return
    const el = document.getElementById(hash)
    if (el) {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }
  }, [location.hash, location.pathname])

  const kpiStats = useMemo(() => {
    const all = mockWorkspaceTasks
    const now = new Date()
    const todayStart = new Date()
    todayStart.setHours(0, 0, 0, 0)
    const todayEnd = new Date(todayStart)
    todayEnd.setDate(todayEnd.getDate() + 1)
    return {
      total: all.length,
      overdue: all.filter((t) => new Date(t.due_at) < now).length,
      dueToday: all.filter((t) => {
        const d = new Date(t.due_at)
        return d >= todayStart && d < todayEnd
      }).length,
    }
  }, [])

  const byPriority = useMemo(() => {
    const map: Record<TaskPriority, WorkspaceTask[]> = { urgent: [], high: [], medium: [], low: [] }
    ;[...mockWorkspaceTasks]
      .sort((a, b) => new Date(a.due_at).getTime() - new Date(b.due_at).getTime())
      .forEach((t) => map[t.priority].push(t))
    return map
  }, [])

  const staffTasksSection = (
    <>
      <Box
        id={WORKSPACE_SECTION_IDS.tongQuan}
        sx={sectionScrollSx}
        component="section"
        aria-label="Tổng quan công việc"
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
            <WorkspaceSectionHeading
              title="Công việc cần xử lý"
              subtitle="Theo vụ án và thời hạn (đơn, thông báo, phiên, quyết định)"
            />
          </Box>
          <Button variant="contained" startIcon={<Add />} sx={{ flexShrink: 0 }}>
            Tạo mới
          </Button>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          <Paper
            variant="outlined"
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              overflow: 'hidden',
              borderTop: 1,
              borderTopColor: 'primary.main',
              borderTopWidth: 3,
              background: (theme) => `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
            }}
          >
            <Typography component="div" sx={DASHBOARD_STAT_LABEL_SX}>
              Tổng công việc
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: 'primary.main', lineHeight: 1.15, mt: 0.5 }}>
              {kpiStats.total}
            </Typography>
          </Paper>
          <Paper
            variant="outlined"
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              overflow: 'hidden',
              borderTop: 1,
              borderTopColor: 'secondary.main',
              borderTopWidth: 3,
              bgcolor: kpiStats.overdue ? (theme) => alpha(theme.palette.secondary.main, 0.06) : 'background.paper',
            }}
          >
            <Typography component="div" sx={DASHBOARD_STAT_LABEL_SX}>
              Quá hạn
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: kpiStats.overdue ? 'secondary.main' : 'text.primary', lineHeight: 1.15, mt: 0.5 }}>
              {kpiStats.overdue}
            </Typography>
          </Paper>
          <Paper
            variant="outlined"
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              overflow: 'hidden',
              borderTop: 1,
              borderTopColor: 'warning.main',
              borderTopWidth: 3,
              background: (theme) => `linear-gradient(145deg, ${alpha(theme.palette.warning.main, 0.12)} 0%, ${alpha(theme.palette.warning.main, 0.02)} 100%)`,
            }}
          >
            <Typography component="div" sx={DASHBOARD_STAT_LABEL_SX}>
              Hạn hôm nay
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: 'warning.dark', lineHeight: 1.15, mt: 0.5 }}>
              {kpiStats.dueToday}
            </Typography>
          </Paper>
        </Box>

        {mockWorkspaceTasks.length === 0 ? (
          <Card sx={{ width: '100%', py: 8, textAlign: 'center', mt: 2 }}>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Không có công việc chờ xử lý
              </Typography>
              <Button variant="outlined" sx={{ mt: 2 }} onClick={() => navigate(workspaceHash(WORKSPACE_SECTION_IDS.lichLamViec))}>
                Xem lịch làm việc
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={2} sx={{ width: '100%', mt: 0.5 }}>
            <Grid item xs={12} md={4}>
              <Paper
                variant="outlined"
                sx={{
                  height: '100%',
                  minHeight: 320,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  overflow: 'hidden',
                  borderTop: 1,
                  borderTopColor: 'error.main',
                  borderTopWidth: 3,
                }}
              >
                <Box sx={{ px: 1.75, py: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: (theme) => alpha(theme.palette.error.main, 0.06) }}>
                  <Typography component="div" sx={DASHBOARD_PRIORITY_TITLE_SX}>
                    KHẨN CẤP
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.8125rem', mt: 0.25 }}>
                    {byPriority.urgent.length} việc
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', maxHeight: { xs: 'none', md: 'calc(100vh - 260px)' } }}>
                  <TaskTabList tasks={byPriority.urgent} emptyLabel="Không có việc." compact />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                variant="outlined"
                sx={{
                  height: '100%',
                  minHeight: 320,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  overflow: 'hidden',
                  borderTop: 1,
                  borderTopColor: 'warning.main',
                  borderTopWidth: 3,
                }}
              >
                <Box sx={{ px: 1.75, py: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08) }}>
                  <Typography component="div" sx={DASHBOARD_PRIORITY_TITLE_SX}>
                    CẦN XỬ LÝ
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.8125rem', mt: 0.25 }}>
                    {byPriority.high.length} việc
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', maxHeight: { xs: 'none', md: 'calc(100vh - 260px)' } }}>
                  <TaskTabList tasks={byPriority.high} emptyLabel="Không có việc." compact />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                variant="outlined"
                sx={{
                  height: '100%',
                  minHeight: 320,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  overflow: 'hidden',
                  borderTop: 1,
                  borderTopColor: 'primary.main',
                  borderTopWidth: 3,
                }}
              >
                <Box sx={{ px: 1.75, py: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06) }}>
                  <Typography component="div" sx={DASHBOARD_PRIORITY_TITLE_SX}>
                    TRUNG BÌNH
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.8125rem', mt: 0.25 }}>
                    {byPriority.medium.length + byPriority.low.length} việc
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', maxHeight: { xs: 'none', md: 'calc(100vh - 260px)' } }}>
                  {byPriority.medium.length === 0 && byPriority.low.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: 'center', px: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Không có việc.
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      {byPriority.medium.length > 0 && <TaskTabList tasks={byPriority.medium} emptyLabel="" compact />}
                      {byPriority.low.length > 0 && (
                        <>
                          {byPriority.medium.length > 0 && (
                            <Typography
                              variant="overline"
                              sx={{
                                display: 'block',
                                px: 1.25,
                                pt: 1.5,
                                pb: 0.5,
                                color: 'text.secondary',
                                fontWeight: 700,
                                borderTop: 1,
                                borderColor: 'divider',
                                bgcolor: 'action.hover',
                              }}
                            >
                              Lịch trình
                            </Typography>
                          )}
                          <TaskTabList tasks={byPriority.low} emptyLabel="" compact />
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>
    </>
  )

  const sharedLowerSections = (
    <>
      <Divider sx={{ my: { xs: 3, sm: 4 } }} role="presentation" />
      <Box id={WORKSPACE_SECTION_IDS.vuAnCuaToi} sx={sectionScrollSx} component="section" aria-label="Vụ án của tôi">
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
            <WorkspaceSectionHeading
              title="Vụ án của tôi"
              subtitle="Danh sách vụ án đang theo dõi"
              sx={{ mb: 0 }}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/cases/new')}
            sx={{ flexShrink: 0, borderRadius: 2, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
          >
            Nộp đơn khởi kiện
          </Button>
        </Box>
        <CasesPage embedded hideTitle hideTopActions />
      </Box>
      <Divider sx={{ my: { xs: 3, sm: 4 } }} role="presentation" />
      <Box id={WORKSPACE_SECTION_IDS.danhSachAn} sx={sectionScrollSx} component="section" aria-label="Danh sách án">
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
            <WorkspaceSectionHeading
              title="Danh sách án"
              subtitle="Toàn bộ vụ án trong hệ thống nội bộ"
              sx={{ mb: 0 }}
            />
          </Box>
          <Button variant="outlined" size="small" startIcon={<Download />} sx={{ flexShrink: 0 }}>
            Xuất Excel
          </Button>
        </Box>
        <WorkspaceCasesPage embedded hideTitle hideTopActions />
      </Box>
      <Divider sx={{ my: { xs: 3, sm: 4 } }} role="presentation" />
      <Box id={WORKSPACE_SECTION_IDS.lichLamViec} sx={sectionScrollSx} component="section" aria-label="Lịch làm việc">
        <WorkspaceSectionHeading title="Lịch làm việc" subtitle="Phiên họp, phiên tòa và xử lý vụ án (6 tháng tới)" />
        <CalendarPage embedded={false} />
      </Box>
    </>
  )

  if (isChiefJudge) {
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box id={WORKSPACE_SECTION_IDS.quanTri} sx={sectionScrollSx} component="section" aria-label="Tổng quan quản trị">
          <WorkspaceSectionHeading
            title="Tổng quan quản trị"
            subtitle="Tổng quan thụ lý và giải quyết án"
          />
          <ChiefJudgeDashboard />
        </Box>
        <Divider sx={{ my: { xs: 3, sm: 4 } }} role="presentation" />

        <Box id={WORKSPACE_SECTION_IDS.vanBanChoKy} sx={sectionScrollSx} component="section" aria-label="Văn bản chờ duyệt/Ký">
          <WorkspaceSectionHeading title="Văn bản chờ duyệt/Ký" subtitle="Văn bản chờ duyệt; ký số văn bản tố tụng (theo luồng)" />
          <DocumentsPendingPage embedded />
        </Box>
        <Divider sx={{ my: { xs: 3, sm: 4 } }} role="presentation" />

        <Box id={WORKSPACE_SECTION_IDS.quyetDinhThamQuyen} sx={sectionScrollSx} component="section" aria-label="Quyết định thẩm quyền">
          <WorkspaceSectionHeading title="Quyết định thẩm quyền" subtitle="Thụ lý/Trả đơn/Chuyển đơn" />
          <PreliminaryChiefPage embedded />
        </Box>
        <Divider sx={{ my: { xs: 3, sm: 4 } }} role="presentation" />

        <Box id={WORKSPACE_SECTION_IDS.phanCongAn} sx={sectionScrollSx} component="section" aria-label="Phân công án">
          <WorkspaceSectionHeading title="Phân công án" subtitle="Danh sách án chờ phân công & gợi ý Thẩm phán" />
          <AssignCaseListPage embedded />
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0 }}>
        {staffTasksSection}
        {isJudge ? (
          <>
            <Divider sx={{ my: { xs: 3, sm: 4 } }} role="presentation" />
            <Box
              id={WORKSPACE_SECTION_IDS.vanBanChoKy}
              sx={sectionScrollSx}
              component="section"
              aria-label="Văn bản chờ duyệt/Ký"
            >
              <WorkspaceSectionHeading
                title="Văn bản chờ duyệt/Ký"
                subtitle="Văn bản chờ duyệt; chuyển Chánh án ký số"
              />
              <DocumentsPendingPage embedded />
            </Box>
          </>
        ) : null}
        {sharedLowerSections}
      </Box>
    </Box>
  )
}
